/* src/components/admin/announcements/dashboard/AnnouncementsDashboard.js */
import React, { useEffect, useMemo, useState } from "react";
import {
  subscribeAnnouncements,
  deleteAnnouncement,
  setPinned,
  subscribeCategories,
  toggleArchive,
} from "../AdminAnnouncementsLogic";
import AnnouncementCard from "./AnnouncementCard";
import ReadReceiptsModal from "./ReadReceiptsModal";
import CategoryManagerModal from "./CategoryManagerModal";
import ToastHost from "./ToastHost";
import { FiSearch } from "react-icons/fi";
import "./AnnouncementsDashboard.css";

function safeCategory(cat) {
  const c = (cat || "").trim();
  return c || "General";
}

function toMs(ts) {
  if (!ts) return null;
  if (typeof ts === "number") return ts;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  return null;
}

function isToday(ms) {
  if (!ms) return false;
  const d = new Date(ms);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export default function AnnouncementsDashboard({ me, onCreate, onEdit, onOpenPost }) {
  const [announcements, setAnnouncements] = useState([]);
  const [savedCategories, setSavedCategories] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [readsModal, setReadsModal] = useState(null);
  const [manageCatsModal, setManageCatsModal] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const unsub = subscribeAnnouncements((rows) => setAnnouncements(rows || []));
    return () => unsub?.();
  }, []);

  useEffect(() => {
    const unsub = subscribeCategories((rows) => setSavedCategories(rows || []));
    return () => unsub?.();
  }, []);

  const allCategories = useMemo(() => {
    const set = new Set();
    set.add("General");
    set.add("Updates");
    savedCategories.forEach((c) => set.add(c.name));
    announcements.forEach((a) => {
      const c = safeCategory(a.category);
      if (c) set.add(c);
    });
    const rest = [...set]
      .filter((c) => c !== "General" && c !== "Updates")
      .sort((a, b) => a.localeCompare(b));
    return ["General", "Updates", ...rest];
  }, [announcements, savedCategories]);

  const filteredFeed = useMemo(() => {
    let rows = [...(announcements || [])];

    // Archive filter
    if (activeFilter === "archive") {
      rows = rows.filter((a) => a.archived === true);
    } else {
      rows = rows.filter((a) => !a.archived);
      if (activeFilter === "pinned") {
        rows = rows.filter((a) => !!a.pinned);
      } else if (activeFilter !== "all") {
        rows = rows.filter((a) => safeCategory(a.category) === activeFilter);
      }
    }

    // Search
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((a) => {
        const title = (a.title || "").toLowerCase();
        const preview = (a.contentText || a.contentHtml || "").toLowerCase();
        const author = (a.createdByName || "").toLowerCase();
        return title.includes(q) || preview.includes(q) || author.includes(q);
      });
    }

    // Sort newest first
    rows.sort((a, b) => {
      const ao = a.createdAtMs || toMs(a.createdAt) || 0;
      const bo = b.createdAtMs || toMs(b.createdAt) || 0;
      return bo - ao;
    });

    return rows;
  }, [announcements, activeFilter, searchQuery]);

  const todayItems = useMemo(
    () => filteredFeed.filter((a) => isToday(a.createdAtMs || toMs(a.createdAt))),
    [filteredFeed]
  );
  const earlierItems = useMemo(
    () => filteredFeed.filter((a) => !isToday(a.createdAtMs || toMs(a.createdAt))),
    [filteredFeed]
  );

  const handleArchiveToggle = async (a) => {
    try {
      await toggleArchive(a.id, a.archived);
      setToast({ kind: "success", msg: a.archived ? "Restored." : "Archived." });
    } catch {
      setToast({ kind: "error", msg: "Action failed." });
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm("Delete this announcement? This cannot be undone.")) return;
    try {
      await deleteAnnouncement(a.id);
      setToast({ kind: "success", msg: "Deleted." });
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Delete failed." });
    }
  };

  const handlePin = async (a, pin) => {
    try {
      await setPinned(a.id, pin);
      setToast({ kind: "success", msg: pin ? "Pinned." : "Unpinned." });
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Pin update failed." });
    }
  };

  const filterPills = [
    { key: "all", label: "All" },
    { key: "pinned", label: "Pinned" },
    ...allCategories.map((c) => ({ key: c, label: c })),
  ];

  return (
    <>
      {/* Topbar */}
      <div className="aaDashTopbar">
        <div className="aaDashTopLeft">
          <h1 className="aaDashTitle">Announcements</h1>
          <p className="aaDashSubtitle">Manage and publish announcements for your team</p>
        </div>
        <div className="aaDashTopRight">
          <div className="aaDashSearchWrap">
            <FiSearch size={15} className="aaDashSearchIcon" />
            <input
              className="aaDashSearchInput"
              placeholder="Search announcements..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="aaDashCreateBtn" onClick={() => onCreate({})}>
            + Create Announcement
          </button>
        </div>
      </div>

      {/* Filter pills */}
      <div className="aaDashPillsRow">
        {filterPills.map((p) => (
          <button
            key={p.key}
            className={`aaDashPill ${activeFilter === p.key ? "active" : ""}`}
            onClick={() => setActiveFilter(p.key)}
          >
            {p.label}
          </button>
        ))}
        <div className="aaDashPillSep" />
        <button
          className={`aaDashPill ${activeFilter === "archive" ? "active" : ""}`}
          onClick={() => setActiveFilter("archive")}
        >
          Archive
        </button>
      </div>

      {/* Feed */}
      <div className="aaDashFeed">
        {filteredFeed.length === 0 ? (
          <div className="aaDashEmpty">No announcements in this view.</div>
        ) : (
          <>
            {todayItems.length > 0 && (
              <div className="aaDashSection">
                <div className="aaDashSectionLabel">TODAY</div>
                {todayItems.map((a) => (
                  <AnnouncementCard
                    key={a.id}
                    announcement={a}
                    onOpenPost={() => onOpenPost(a.id)}
                    onEdit={() => onEdit(a.id)}
                    onDelete={() => handleDelete(a)}
                    onPin={() => handlePin(a, !a.pinned)}
                    onArchive={() => handleArchiveToggle(a)}
                    onOpenReads={() => setReadsModal(a)}
                    onToast={setToast}
                  />
                ))}
              </div>
            )}
            {earlierItems.length > 0 && (
              <div className="aaDashSection">
                {filteredFeed.length > todayItems.length && (
                  <div className="aaDashSectionLabel">EARLIER</div>
                )}
                {earlierItems.map((a) => (
                  <AnnouncementCard
                    key={a.id}
                    announcement={a}
                    onOpenPost={() => onOpenPost(a.id)}
                    onEdit={() => onEdit(a.id)}
                    onDelete={() => handleDelete(a)}
                    onPin={() => handlePin(a, !a.pinned)}
                    onArchive={() => handleArchiveToggle(a)}
                    onOpenReads={() => setReadsModal(a)}
                    onToast={setToast}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {readsModal && (
        <ReadReceiptsModal
          me={me}
          announcement={readsModal}
          onClose={() => setReadsModal(null)}
        />
      )}
      {manageCatsModal && (
        <CategoryManagerModal onClose={() => setManageCatsModal(false)} />
      )}
      <ToastHost toast={toast} onClear={() => setToast(null)} />
    </>
  );
}
